"use client";

import * as React from "react";
import dynamic from "next/dynamic";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { AnimatePresence, motion } from "framer-motion";
import { TimeSVG, CalendarSVG, TagSVG } from "@/components/ui/SVG";
import type { TagOption } from "@/cms/editor/TagPickerPanel";
import type { AuthorSummary } from "@/cms/lib/authors";
import { getLocalizedUserName } from "@/utils/userLocalization";

const DatePicker = dynamic(() => import("react-multi-date-picker"), { ssr: false });

type AuthorFieldProps = {
  label: string;
  locale: "en" | "fa";
  query: string;
  selectedAuthor: AuthorSummary | null;
  results: AuthorSummary[];
  loading: boolean;
  disabled: boolean;
  onQueryChange: (value: string) => void;
  onSelect: (author: AuthorSummary) => void;
  onClear: () => void;
};

function AuthorField({
  label,
  locale,
  query,
  selectedAuthor,
  results,
  loading,
  disabled,
  onQueryChange,
  onSelect,
  onClear,
}: AuthorFieldProps) {
  const [focused, setFocused] = React.useState(false);
  const activeResults = React.useMemo(() => {
    if (!query.trim()) return [];
    return results.filter((author) => author.id !== selectedAuthor?.id);
  }, [query, results, selectedAuthor?.id]);
  const displayName = selectedAuthor
    ? getLocalizedUserName(locale, selectedAuthor).fullName || selectedAuthor.email
    : "";
  const showDropdown = focused && activeResults.length > 0;

  return (
    <div className="space-y-2">
      <label className="text-xs font-semibold text-slate-600">{label}</label>
      <div className="space-y-2">
        {selectedAuthor ? (
          <div className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-slate-50 px-3 py-2 text-sm">
            <div className="min-w-0">
              <div className="truncate font-medium text-slate-800">{displayName}</div>
              <div className="truncate text-xs text-slate-500">{selectedAuthor.email}</div>
            </div>
            <button
              type="button"
              className="rounded-md border border-gray-200 px-2 py-1 text-xs text-slate-600 hover:border-pinkCustom hover:text-pinkCustom disabled:opacity-60"
              onClick={onClear}
              disabled={disabled}
            >
              Clear
            </button>
          </div>
        ) : null}

        {!selectedAuthor ? (
          <div className="relative">
            <input
              type="text"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-pinkCustom disabled:bg-slate-100 disabled:text-slate-400"
              placeholder="Search operator by name or email..."
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              disabled={disabled}
              dir={locale === "fa" ? "rtl" : "ltr"}
              onFocus={() => setFocused(true)}
              onBlur={() => setTimeout(() => setFocused(false), 150)}
            />
            {loading ? (
              <p className="mt-1 text-xs text-slate-400">Searching...</p>
            ) : null}
            <AnimatePresence initial={false}>
              {showDropdown ? (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.15 }}
                  className="absolute z-30 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-sm"
                >
                  {activeResults.map((author) => {
                    const name = getLocalizedUserName(locale, author).fullName || author.email;
                    return (
                      <button
                        key={author.id}
                        type="button"
                        className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-slate-50"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => onSelect(author)}
                      >
                        <span className="font-medium text-slate-800">{name}</span>
                        <span className="text-xs text-slate-500">{author.email}</span>
                      </button>
                    );
                  })}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export type DetailsPanelMobileProps = {
  open: boolean;
  onToggleOpen: () => void;
  isArchived: boolean;

  durationValue: string;
  durationUnit: "min" | "sec";
  onDurationInputChange: (value: string) => void;
  onDurationUnitChange: (unit: "min" | "sec") => void;

  formattedDisplayDate: { en: string; fa: string } | null;
  datePickerRef: React.RefObject<any>;
  datePickerValue: DateObject | null;
  onDatePickerChange: (value: DateObject | DateObject[] | null) => void;
  openDatePicker: () => void;

  selectedTags: TagOption[];
  tagsDisabled: boolean;
  onOpenTagManager: () => void;

  authorFaQuery: string;
  authorEnQuery: string;
  authorFa: AuthorSummary | null;
  authorEn: AuthorSummary | null;
  authorSearchResults: AuthorSummary[];
  authorSearchLoading: boolean;
  onAuthorQueryChange: (locale: "en" | "fa", value: string) => void;
  onAuthorSelect: (locale: "en" | "fa", author: AuthorSummary) => void;
  onAuthorClear: (locale: "en" | "fa") => void;
};

export function DetailsPanelMobile({
  open,
  onToggleOpen,
  isArchived,
  durationValue,
  durationUnit,
  onDurationInputChange,
  onDurationUnitChange,
  formattedDisplayDate,
  datePickerRef,
  datePickerValue,
  onDatePickerChange,
  openDatePicker,
  selectedTags,
  tagsDisabled,
  onOpenTagManager,
  authorFaQuery,
  authorEnQuery,
  authorFa,
  authorEn,
  authorSearchResults,
  authorSearchLoading,
  onAuthorQueryChange,
  onAuthorSelect,
  onAuthorClear,
}: DetailsPanelMobileProps) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <button
        type="button"
        className="flex w-full items-center justify-between text-left text-sm font-semibold text-slateCustom"
        onClick={onToggleOpen}
      >
        <span>Details</span>
        <span className="text-xs text-slateCustom/70">{open ? "Hide" : "Show"}</span>
      </button>

      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-3 space-y-4 border-t border-gray-100 pt-4">
              <div className="flex items-center gap-2">
                <TimeSVG className="h-5 w-5 text-slate-500" />
                <div className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 px-3 py-2 text-xs">
                  <input
                    type="text"
                    className="w-24 border-r border-gray-200 bg-transparent px-1 py-1 text-sm outline-none disabled:text-slate-400"
                    value={durationValue}
                    onChange={(event) => onDurationInputChange(event.target.value)}
                    disabled={isArchived}
                    inputMode="numeric"
                  />
                  <div className="flex divide-x divide-gray-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => onDurationUnitChange("min")}
                      className={`px-2 ${durationUnit === "min" ? "text-pinkCustom" : "text-gray-500"}`}
                      disabled={isArchived}
                    >
                      Min
                    </button>
                    <button
                      type="button"
                      onClick={() => onDurationUnitChange("sec")}
                      className={`px-2 ${durationUnit === "sec" ? "text-pinkCustom" : "text-gray-500"}`}
                      disabled={isArchived}
                    >
                      Sec
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <CalendarSVG className="mt-1 h-5 w-5 text-slate-500" />
                <div className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 px-3 py-2 text-xs">
                  <button
                    type="button"
                    className="bg-slateCustom hover:bg-pinkCustom rounded-md px-3 py-1.5 font-medium text-white transition disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={openDatePicker}
                    disabled={isArchived}
                    title="Pick display date"
                  >
                    Pick
                  </button>
                  <DatePicker
                    ref={datePickerRef}
                    value={datePickerValue}
                    onChange={onDatePickerChange}
                    calendar={persian}
                    locale={persian_fa}
                    calendarPosition="bottom-center"
                    editable={false}
                    inputClass="w-32 h-9 opacity-0 absolute left-0 top-0"
                    containerClassName="relative"
                    style={{ opacity: 0, pointerEvents: "none" }}
                    portal
                    render={<input className="hidden" readOnly />}
                  />
                  <span className="text-xs text-slate-600">
                    {formattedDisplayDate ? (
                      <>
                        <span dir="ltr">{formattedDisplayDate.en}</span>
                        {" | "}
                        <span dir="rtl">{formattedDisplayDate.fa}</span>
                      </>
                    ) : (
                      "Not set"
                    )}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <TagSVG className="h-5 w-5 text-slate-500" />
                <div className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 px-3 py-2 text-xs">
                  <div className="grid grid-cols-4 gap-1">
                    {selectedTags.length === 0 ? (
                      <span className="col-span-4 text-slate-500">No tags assigned.</span>
                    ) : (
                      selectedTags.map((tag) => (
                        <span
                          key={tag.id}
                          className="col-span-2 truncate rounded bg-slate-50 px-2 py-1 text-slate-700"
                        >
                          {tag.name_en}
                        </span>
                      ))
                    )}
                  </div>
                  <button
                    type="button"
                    className="bg-slateCustom hover:bg-pinkCustom rounded-md px-3 py-1.5 font-medium text-white transition disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={onOpenTagManager}
                    disabled={tagsDisabled}
                    title="Manage assigned tags"
                  >
                    Manage
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <AuthorField
                  label="Persian Author"
                  locale="fa"
                  query={authorFaQuery}
                  selectedAuthor={authorFa}
                  results={authorSearchResults}
                  loading={authorSearchLoading}
                  disabled={isArchived}
                  onQueryChange={(value) => onAuthorQueryChange("fa", value)}
                  onSelect={(author) => onAuthorSelect("fa", author)}
                  onClear={() => onAuthorClear("fa")}
                />
                <AuthorField
                  label="English Author"
                  locale="en"
                  query={authorEnQuery}
                  selectedAuthor={authorEn}
                  results={authorSearchResults}
                  loading={authorSearchLoading}
                  disabled={isArchived}
                  onQueryChange={(value) => onAuthorQueryChange("en", value)}
                  onSelect={(author) => onAuthorSelect("en", author)}
                  onClear={() => onAuthorClear("en")}
                />
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

